import {
  Timestamp,
  Transaction,
  Unsubscribe,
  arrayRemove,
  arrayUnion,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  updateDoc,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import { Dish, FirestoreDishDocument } from "@dashboard/types/dish";

import { readDish, writeDish } from "./firestore-converter/dish";

export interface DishApi {
  fetchDishes(restaurantUuid: string): Promise<Dish[]>;
  subscribeToDishes(
    restaurantUuid: string,
    onChange: (dishes: Dish[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe;
  setDish(dish: Dish, isNew: boolean): Promise<void>;
  addProductToDish(
    restaurantUuid: string,
    dishId: string,
    productId: string
  ): Promise<void>;
  removeProductFromDish(
    restaurantUuid: string,
    dishId: string,
    productId: string
  ): Promise<void>;
  deleteDish(restaurantUuid: string, dishId: string): Promise<void>;
}

/**
 * An implementation of the DishApi that uses Firestore as the backend.
 */
export class FirestoreBackedDishApi implements DishApi {
  private firestoreRefs: FirestoreRefs;
  private beforeDeleteDishFromMenuSection: (
    transaction: Transaction,
    restaurantUuid: string,
    dishId: string
  ) => Promise<void>;

  constructor(params: {
    firestoreRefs: FirestoreRefs;
    beforeDeleteDishFromMenuSection: (
      transaction: Transaction,
      restaurantUuid: string,
      dishId: string
    ) => Promise<void>;
  }) {
    this.firestoreRefs = params.firestoreRefs;
    this.beforeDeleteDishFromMenuSection =
      params.beforeDeleteDishFromMenuSection;
  }

  public fetchDishes(restaurantUuid: string): Promise<Dish[]> {
    return getDocs(
      query(
        this.firestoreRefs.dishes(restaurantUuid),
        // createdAt is always set (for all dishes), and is used to order the dishes.
        orderBy("createdAt", "desc")
      )
    ).then((querySnapshot) =>
      querySnapshot.docs
        .map((doc) => doc.data() as FirestoreDishDocument)
        .map(readDish)
    );
  }

  public subscribeToDishes(
    restaurantUuid: string,
    onChange: (dishes: Dish[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      query(
        this.firestoreRefs.dishes(restaurantUuid),
        orderBy("createdAt", "desc")
      ),
      { includeMetadataChanges: true },
      (snapshot) => {
        // A pending write only exists in the local cache; the acked snapshot follows.
        if (snapshot.metadata.hasPendingWrites) {
          return;
        }
        onChange(
          snapshot.docs
            .map((doc) => doc.data() as FirestoreDishDocument)
            .map(readDish)
        );
      },
      onError
    );
  }

  public setDish(dish: Dish, isNew: boolean): Promise<void> {
    // The drawer holds productIds from when it was opened; a product deleted
    // since then would otherwise be written back as a dangling reference.
    return runTransaction(this.firestoreRefs.firestore, async (transaction) => {
      const products = await Promise.all(
        dish.productIds.map((id) =>
          transaction.get(this.firestoreRefs.product(dish.restaurantUuid, id))
        )
      );
      const productIds = dish.productIds.filter((_, i) => products[i].exists());
      // optionalProductIds is a flag on productIds members.
      const optionalProductIds = dish.optionalProductIds.filter((id) =>
        productIds.includes(id)
      );
      const firestoreDish = writeDish(
        { ...dish, productIds, optionalProductIds },
        isNew
      );
      transaction.set(
        this.firestoreRefs.dish(dish.restaurantUuid, dish.id),
        firestoreDish,
        { merge: true }
      );
    });
  }

  // Field-level writes so concurrent editors of the same dish cannot
  // overwrite each other's productIds. updateDoc fails if the dish
  // document no longer exists, instead of recreating it like setDoc.
  public addProductToDish(
    restaurantUuid: string,
    dishId: string,
    productId: string
  ): Promise<void> {
    return updateDoc(this.firestoreRefs.dish(restaurantUuid, dishId), {
      productIds: arrayUnion(productId),
      modifiedAt: Timestamp.now(),
    });
  }

  public removeProductFromDish(
    restaurantUuid: string,
    dishId: string,
    productId: string
  ): Promise<void> {
    // optionalProductIds is a flag on productIds members.
    return updateDoc(this.firestoreRefs.dish(restaurantUuid, dishId), {
      productIds: arrayRemove(productId),
      optionalProductIds: arrayRemove(productId),
      modifiedAt: Timestamp.now(),
    });
  }

  // This would be moved to an API call and handled in the backend later on
  public deleteDish(restaurantUuid: string, dishId: string): Promise<void> {
    return runTransaction(this.firestoreRefs.firestore, async (transaction) => {
      await this.beforeDeleteDishFromMenuSection(
        transaction,
        restaurantUuid,
        dishId
      );
      // deleting the dish itself is not managed by the previous function
      await transaction.delete(this.firestoreRefs.dish(restaurantUuid, dishId));
    });
  }
}
